import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff, Shield, KeyRound } from "lucide-react";

const ResetPassword = () => {
  const { API } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setTokenError("No reset token provided");
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API}/auth/verify-reset-token?token=${token}`);
      const data = await response.json();

      if (data.valid) {
        setTokenValid(true);
        setEmail(data.email || "");
      } else {
        setTokenError(data.error || "Invalid token");
      }
    } catch (error) {
      setTokenError("Failed to verify token");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ token, new_password: password });
      const response = await fetch(`${API}/auth/reset-password?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        toast.error(data.detail || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (password.length === 0) return { strength: 0, text: "", color: "" };
    if (password.length < 6) return { strength: 25, text: "Weak", color: "bg-red-500" };
    if (password.length < 8) return { strength: 50, text: "Fair", color: "bg-yellow-500" };
    if (password.length < 12) return { strength: 75, text: "Good", color: "bg-rnb-blue" };
    return { strength: 100, text: "Strong", color: "bg-rnb-teal" };
  };

  const passwordStrength = getPasswordStrength();

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="card-modern p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rnb-blue mx-auto mb-4"></div>
          <p className="text-rnb-gray">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="card-modern p-8 text-center" data-testid="invalid-token-card">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
              Invalid Reset Link
            </h1>
            <p className="text-rnb-gray mb-6">
              {tokenError === "Token expired"
                ? "This password reset link has expired."
                : tokenError === "Token already used"
                ? "This password reset link has already been used."
                : "This password reset link is invalid or has expired."}
            </p>

            <div className="space-y-3">
              <Link to="/forgot-password">
                <Button className="w-full h-12 btn-primary">Request New Link</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full h-12">
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 w-full max-w-md">
          <div className="card-modern p-8 text-center" data-testid="success-card">
            <div className="w-20 h-20 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
              <CheckCircle className="h-10 w-10 text-rnb-teal" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
              Password Reset Complete!
            </h1>
            <p className="text-rnb-gray mb-6">
              Your password has been successfully changed. You can now log in with your new password.
            </p>

            <Button
              className="w-full h-12 btn-primary"
              onClick={() => navigate("/login")}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex animate-fade-in">
      {/* Left Side - Brand Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <img
          src="https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Professional healthcare consulting"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-xl">RNB Consulting</span>
          </Link>
          
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-4xl font-bold mb-4 leading-tight">
                Create New Password
              </h2>
              <p className="text-lg text-white/80 max-w-md">
                Choose a strong password to keep your account secure. We recommend using a mix of letters, numbers, and symbols.
              </p>
            </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <h3 className="font-semibold text-lg mb-3">Password Tips</h3>
              <ul className="space-y-2 text-white/80 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-rnb-teal" />
                  At least 8 characters long
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-rnb-teal" />
                  Mix of upper and lowercase letters
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-rnb-teal" />
                  Include numbers and symbols
                </li>
              </ul>
            </div>
          </div>

          <p className="text-sm text-white/60">
            Need help? Contact support@rnbconsultinggroup.com
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-rnb-blue/5 rounded-full blur-3xl translate-y-1/2" />
        
        <div className="relative z-10 w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="flex lg:hidden items-center justify-center space-x-2 mb-8">
            <div className="w-12 h-12 bg-rnb-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-2xl text-rnb-navy">RNB Consulting</span>
          </Link>

          <div className="card-modern p-8" data-testid="reset-password-card">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-rnb-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <KeyRound className="h-8 w-8 text-rnb-blue" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                Create New Password
              </h1>
              {email && (
                <p className="text-rnb-gray">
                  For <span className="font-semibold text-rnb-navy">{email}</span>
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-rnb-navy font-medium">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-rnb-gray" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 border-gray-200 focus:border-rnb-teal focus:ring-rnb-teal/20"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-rnb-gray hover:text-rnb-navy transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {password && (
                  <div className="space-y-1">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <p className="text-xs text-rnb-gray">
                      Password strength: <span className="font-medium">{passwordStrength.text}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-rnb-navy font-medium">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-rnb-gray" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-11 h-12 border-gray-200 focus:border-rnb-teal focus:ring-rnb-teal/20"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary text-base font-semibold"
                disabled={loading || password !== confirmPassword}
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Resetting...
                  </span>
                ) : (
                  "Reset Password"
                )}
              </Button>

              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full h-12" type="button">
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </form>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-rnb-gray">
            <Shield className="h-4 w-4" />
            <span>Your data is protected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
